import { useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';

import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Stack from '@mui/material/Stack';
import { GridColDef } from '@mui/x-data-grid';

import Modal from '../../common/Modal';
import SearchField from '../../common/searchField';
import DataTable from '../../components/DataTable';

import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE } from '../../utils/table-utils';
import { connectivityDashboardActions } from '../../store/reducers/connectivity-dashboard';
import { BackendServerRequestPayloadTypes } from '../../store/models/connectivity-dashboard';

const Styles = {
  container: { boxShadow: '0px 0px 14px 0px rgba(0, 0, 0, 0.02)', borderRadius: '18px' },
  paper: {
    margin: 'auto',
    boxShadow: '0px 0px 14px 0px #0000000D',
    borderRadius: '18px',
    border: `1px solid #D9DFED`,
    height: '80vh',
    overflow: 'hidden',
  },
  header: {
    p: 3,
    display: 'flex',
    justifyContent: 'flex-end',
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    boxSizing: 'border-box',
    // height: '87px',
  },
  searchFieldWrapper: { display: 'flex', alignItems: 'center', columnGap: 3 },
  dataTableWrapper: {
    height: '100%',
    overflow: 'hidden',
  },
  dataTable: {
    '& .MuiDataGrid-virtualScroller': {
      overflow: 'auto',
    },
    '&.MuiDataGrid-root': {
      height: 'calc(80vh - 196px)',
      minHeight: 'calc(80vh - 196px)',
    },
  },
  closeBtnWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    paddingBlock: 4,
    borderTop: '1px solid #D9DFED',
  },
  tableCellText: {
    fontSize: '16px',
    fontWeight: 400,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
};

interface BackendServerLabelTypes {
  all: string;
  connected: string;
  disconnected: string;
}

const backendServerLabel: BackendServerLabelTypes = {
  all: '',
  connected: 'CONNECTED',
  disconnected: 'DISCONNECTED',
};

const BackendServerTable = ({
  onModalClose,
  type,
  selectedOntology,
  backendServer,
  backendServerLoading,
  backendServerPageNumber,
  backendServerTotalCount,
}: any) => {
  const dispatch = useDispatch();
  const [sortingModel, setSortingModel] = useState({ field: '', sort: '' });
  const [searchValue, setSearchValue] = useState('');

  const dispatchBackendServerRequest = (payload: BackendServerRequestPayloadTypes) => {
    dispatch(
      connectivityDashboardActions.getBackendServerRequest({
        connectivity_status: type,
        search_item: searchValue,
        ...sortingModel,
        ...payload,
      })
    );
  };

  const onSearch = (value: string) => {
    dispatchBackendServerRequest({ limit: DEFAULT_PAGE_SIZE, page: DEFAULT_PAGE_NUMBER, search_item: value });
    setSearchValue(value);
  };

  useEffect(() => {
    if (type) {
      dispatchBackendServerRequest({ limit: DEFAULT_PAGE_SIZE, page: DEFAULT_PAGE_NUMBER });
    }
  }, [type]);

  const columns: GridColDef[] = [
    {
      field: 'uid',
      headerName: 'ID',
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      minWidth: 100,
      renderCell: (params) => (
        <Tooltip title={params.value} arrow>
          <Typography component='p' sx={Styles.tableCellText}>
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'container',
      headerName: 'Container',
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      minWidth: 100,
      renderCell: (params) => (
        <Tooltip title={params.value} arrow>
          <Typography component='p' sx={Styles.tableCellText}>
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'endpoint',
      headerName: 'End Point',
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      minWidth: 100,
      renderCell: (params) => (
        <Tooltip title={params.value} arrow>
          <Typography component='p' sx={Styles.tableCellText}>
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'image_spec',
      headerName: 'Image Spec',
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      minWidth: 100,
      renderCell: (params) => (
        <Tooltip title={params.value} arrow>
          <Typography component='p' sx={Styles.tableCellText}>
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'namespace',
      headerName: 'Name Space',
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      minWidth: 100,
      renderCell: (params) => (
        <Tooltip title={params.value} arrow>
          <Typography component='p' sx={Styles.tableCellText}>
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'service',
      headerName: 'Service',
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      minWidth: 100,
      renderCell: (params) => (
        <Tooltip title={params.value} arrow>
          <Typography component='p' sx={Styles.tableCellText}>
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
  ];

  return (
    <Modal
      title={`LIST OF ${backendServerLabel[type as keyof BackendServerLabelTypes]} BACKEND SERVER`}
      open
      customStyles={{ width: '60%', maxWidth: '60%' }}
      handleClose={onModalClose}
    >
      <Container maxWidth={false} disableGutters sx={Styles.container}>
        <Paper sx={Styles.paper}>
          <Box sx={Styles.header}>
            <Box sx={Styles.searchFieldWrapper}>
              <SearchField searchPlaceholder='Search' onSearch={onSearch} width='100%' />
            </Box>
          </Box>
          <Box sx={Styles.dataTableWrapper}>
            <DataTable
              columns={columns}
              rows={backendServer}
              loading={backendServerLoading}
              tableStyles={Styles.dataTable}
              noRowsOverlay={() => (
                <Stack height='100%' alignItems='center' justifyContent='center'>
                  No Backend Server Data
                </Stack>
              )}
              isServerPagination
              totalRowCount={backendServerTotalCount}
              pageNumber={backendServerPageNumber}
              onPagination={(pageSize: number, pageNumber: number) => {
                dispatchBackendServerRequest({
                  limit: pageSize,
                  page: pageNumber,
                });
              }}
              onSorting={(sortModel: { field: string; sort: string }) => {
                dispatchBackendServerRequest({
                  limit: DEFAULT_PAGE_SIZE,
                  page: backendServerPageNumber,
                  ...sortModel,
                });
                setSortingModel(sortModel);
              }}
              otherProps={{
                getRowId: (row: any) => row.uid,
              }}
            />
            <Box sx={Styles.closeBtnWrapper}>
              <Button variant='outlined' onClick={onModalClose}>
                Close
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Modal>
  );
};

const mapStateToProps = (state: any) => ({
  backendServer: state.connectivityDashboard.backendServer,
  backendServerLoading: state.connectivityDashboard.backendServerLoading,
  backendServerPageNumber: state.connectivityDashboard.backendServerPageNumber,
  backendServerTotalCount: state.connectivityDashboard.backendServerTotalCount,
});

export default connect(mapStateToProps)(BackendServerTable);
